import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private currencySymbolSubject = new BehaviorSubject<string>('');
  public currencySymbol$: Observable<string> = this.currencySymbolSubject.asObservable();
  private clientData: any = null;

  constructor() {
    // Initialize from sessionStorage if available
    const cachedSymbol = sessionStorage.getItem('currencySymbol');
    if (cachedSymbol) {
      this.currencySymbolSubject.next(cachedSymbol);
    }
    
    // Load clientData from sessionStorage
    const clientDataString = sessionStorage.getItem('clientData');
    if (clientDataString) {
      try {
        this.clientData = JSON.parse(clientDataString);
      } catch (e) {
        console.error('Error parsing clientData from sessionStorage:', e);
      }
    }
  }

  /**
   * Updates the client data reference
   */
  setClientData(clientData: any): void {
    this.clientData = clientData;
    // Store in sessionStorage for persistence
    if (clientData) {
      sessionStorage.setItem('clientData', JSON.stringify(clientData));
    }
  }

  /**
   * Checks if the clientData has valid currency information
   * Valid currency means:
   * - currency object exists and has at least a non-empty code or symbol, OR
   * - currencyId is present and currency object is provided by the backend
   */
  hasValidClientCurrency(clientData: any): boolean {
    if (!clientData) {
      return false;
    }

    // Check if currency object exists and has valid data
    if (clientData.currency) {
      const currency = clientData.currency;
      // Valid if it has at least a non-empty code or symbol
      if ((currency.code && currency.code.trim() !== '') || 
          (currency.symbol && currency.symbol.trim() !== '')) {
        return true;
      }
    }

    // Check if currencyId is present (backend should provide currency object in this case)
    if (clientData.currencyId && clientData.currency) {
      return true;
    }

    return false;
  }

  /**
   * Gets currency symbol from client currency, with fallback to code if symbol is missing
   */
  getClientCurrencySymbol(clientData: any): string | null {
    if (!clientData?.currency) {
      return null;
    }

    const currency = clientData.currency;
    // Prefer symbol, fallback to code if symbol is missing
    if (currency.symbol && currency.symbol.trim() !== '') {
      return currency.symbol;
    }
    if (currency.code && currency.code.trim() !== '') {
      return currency.code;
    }

    return null;
  }

  /**
   * Loads currency from the client/shop data model.
   * Currency is always available in clientData.currency.
   */
  loadCurrency(clientData: any): void {
    // Store clientData for second currency checks
    this.setClientData(clientData);

    // Get currency from client data
    if (this.hasValidClientCurrency(clientData)) {
      const clientCurrencySymbol = this.getClientCurrencySymbol(clientData);
      if (clientCurrencySymbol) {
        this.setCurrencySymbol(clientCurrencySymbol);
        return;
      }
    }

    console.warn('No valid currency found in client data');
  }

  /**
   * Sets the currency symbol and updates both observable and sessionStorage
   */
  setCurrencySymbol(symbol: string): void {
    this.currencySymbolSubject.next(symbol);
    sessionStorage.setItem('currencySymbol', symbol);
  }

  /**
   * Gets the current currency symbol synchronously
   */
  getCurrencySymbol(): string {
    return this.currencySymbolSubject.value;
  }

  /**
   * Removes trailing zeros from a price string (iOS parity: removeTrailingZeros).
   * Whole numbers drop decimals ("150.00" -> "150"), fractional keep minimal
   * digits ("0.03" -> "0.03", "2.5" -> "2.5"). Non-numeric input is returned as-is.
   */
  removeTrailingZeros(price: string | number): string {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;

    if (!isFinite(priceNum)) {
      return String(price);
    }

    if (Number.isInteger(priceNum)) {
      return String(priceNum);
    }

    // Up to 2 fraction digits, trailing zeros stripped, dot separator.
    return parseFloat(priceNum.toFixed(2)).toString();
  }

  /**
   * Formats a primary-currency price value (number only, no symbol — the symbol
   * is rendered separately in the templates). Mirrors iOS formatPrice.
   */
  formatPrice(price: string | number): string {
    return this.removeTrailingZeros(price);
  }

  /**
   * Checks if second currency is active
   * Second currency is active when all of the following are present:
   * - second_currency_id is non-nil
   * - currency_transformer is non-nil and greater than 0
   * - second_currency is non-nil and has at least basic info (symbol or code)
   */
  isSecondCurrencyActive(clientData?: any): boolean {
    const data = clientData || this.clientData;
    if (!data) {
      return false;
    }

    // Check if second_currency_id is present
    if (!data.second_currency_id) {
      return false;
    }

    // Check if currency_transformer is present and greater than 0
    if (!data.currency_transformer || data.currency_transformer <= 0) {
      return false;
    }

    // Check if second_currency exists and has at least symbol or code
    if (!data.second_currency) {
      return false;
    }

    const secondCurrency = data.second_currency;
    if ((!secondCurrency.symbol || secondCurrency.symbol.trim() === '') &&
        (!secondCurrency.code || secondCurrency.code.trim() === '')) {
      return false;
    }

    return true;
  }

  /**
   * Calculates the second price using currency_transformer
   * Returns formatted string with 2 decimal places
   */
  getSecondPrice(price: string | number, clientData?: any): string | null {
    if (!this.isSecondCurrencyActive(clientData)) {
      return null;
    }

    const data = clientData || this.clientData;
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    
    if (isNaN(priceNum)) {
      return null;
    }

    const transformer = data.currency_transformer;
    const secondPrice = priceNum * transformer;
    // iOS parity (formatSecondPriceWithCalculation): min 0 / max 2 fraction
    // digits, trailing zeros stripped (e.g. 2.00 -> "2", 0.0345 -> "0.03").
    return this.removeTrailingZeros(secondPrice);
  }

  /**
   * Gets the second currency symbol
   * Prefers symbol, falls back to code if symbol is missing
   */
  getSecondCurrencySymbol(clientData?: any): string | null {
    if (!this.isSecondCurrencyActive(clientData)) {
      return null;
    }

    const data = clientData || this.clientData;
    if (!data.second_currency) {
      return null;
    }

    const secondCurrency = data.second_currency;
    // Prefer symbol, fallback to code if symbol is missing
    if (secondCurrency.symbol && secondCurrency.symbol.trim() !== '') {
      return secondCurrency.symbol;
    }
    if (secondCurrency.code && secondCurrency.code.trim() !== '') {
      return secondCurrency.code;
    }

    return null;
  }
}
